import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, exhaustMap, switchMap, forkJoin, tap, of, withLatestFrom, take, filter } from 'rxjs';
import { LocumsMarginCalculatorEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-margin-calculator-events.enum';
import { LocumsContractRequestsEvents } from 'src/app/recruiting/locums-margin-calculator/store/enums/locums-contract-request-events.enum';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { JobEffects } from 'src/app/recruiting/locums-margin-calculator/store/effects/job.effects';
import { CandidateEffects } from 'src/app/recruiting/locums-margin-calculator/store/effects/candidate.effects';
import { AdditionalRatesEffects } from 'src/app/recruiting/locums-margin-calculator/store/effects/additional-rates.effects';
import { MarginExpensesEffects } from './margin-expenses.effects';
import { LocumsMarginCalculatorService } from '../../services/locums-margin-calculator.service';
import { LocumsMarginCalculatorStore } from '../locums-margin-calculator.store';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { LocumsMarginCalculatorSlice } from '../locums-margin-calculator.slice';
import { MarginCalculationsEffects } from 'src/app/recruiting/locums-margin-calculator/store/effects/margin-calculations.effects';
import { LocumsMarginCalculatorValidationResult } from '../../models/locums-margin-calculator-validation-result.model';
import { LocumsMarginCalcSections } from '../../helpers/locums-margin-calc-sections-constants';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

@Injectable({ providedIn: 'root' })
export class LocumsMarginCalculatorEffects {
    private readonly eventBus = inject(EventBusService);
    private readonly jobEffects = inject(JobEffects);
    private readonly candidateEffects = inject(CandidateEffects);
    private readonly additionalRatesEffects = inject(AdditionalRatesEffects);
    private readonly marginExpensesEffects = inject(MarginExpensesEffects);
    private readonly marginCalculationsEffects = inject(MarginCalculationsEffects);
    private readonly marginCalculatorService = inject(LocumsMarginCalculatorService);
    private readonly marginCalculatorStore = inject(LocumsMarginCalculatorStore);
    private readonly toasterService = inject(ToasterService);
    private readonly validationResults = new Map<string, LocumsMarginCalculatorValidationResult>();
    private readonly _ldFeatureManager = inject(LDFeatureManager);

    private currentAction: 'save' | 'createPresentation' | 'contractRequest' | null = null;

    private readonly enableSpecialtyPersistenceLocums1099$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.EnableSpecialtyPersistenceLocums1099
    );

    private readonly enableCreatePresentation$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.Locums1099SubmittalCreatePresentation
    );

    private readonly novaLocumsLeave1099GmcDialog$ = this._ldFeatureManager.isEnabled(
        FeatureFlag.NovaLocumsLeave1099GmcDialog
    );

    constructor() {
        this.listenToLocums1099MarginCalculatorPageOpenedEvent();
        this.listenToLocums1099MarginCalculatorSaveSucceededEvent();
        this.listenToLocums1099MarginPipelineRequestSaveSucceededEvent();
        this.listenToLocums1099MarginCalculatorExistingMarginPageOpenedEvent();
        this.listenToLocums1099MarginCalculatorValidationEvents();
        this.listenToLocums1099MarginPendingRequestSaveSucceededEvent();
        this.listenToLocums1099MarginPipelineRequestUpdatedSucceededEvent();
    }

    private listenToLocums1099MarginCalculatorPageOpenedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_PAGE_OPENED)
            .subscribe((event: EventPayload) => {
                this.jobEffects.getJobDetails(event.payload.jobId);
                this.candidateEffects.getCandidateDetails(event.payload.candidateId);
                this.getLookups();
            });
    }

    private listenToLocums1099MarginCalculatorExistingMarginPageOpenedEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_EXISTING_MARGIN_PAGE_OPENED)
            .subscribe((event: EventPayload) => {
                this.getLookups();
                this.getMarginDetails(event);
            });
    }

    private listenToLocums1099MarginCalculatorValidationEvents() {
        // Validation triggers
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_BUTTON_CLICKED)
            .subscribe(() => this.startValidation('save'));

        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_CONTRACT_REQUEST_BUTTON_CLICKED)
            .subscribe(() => this.startValidation('contractRequest'));

        // Validation result events
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_CANDIDATE_VALIDATION_RESULT)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$))
            .subscribe(([event, featureFlagEnabled]) => {
                this.setValidationResult(LocumsMarginCalcSections.CANDIDATE, event, featureFlagEnabled);
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_MARGIN_SETUP_VALIDATION_RESULT)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$))
            .subscribe(([event, featureFlagEnabled]) => {
                this.setValidationResult(LocumsMarginCalcSections.MARGIN_SETUP, event, featureFlagEnabled);
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_ADDITIONAL_RATES_VALIDATION_RESULT)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$))
            .subscribe(([event, featureFlagEnabled]) => {
                this.setValidationResult(LocumsMarginCalcSections.ADDITIONAL_RATES, event, featureFlagEnabled);
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_EXPENSES_VALIDATION_RESULT)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$))
            .subscribe(([event, featureFlagEnabled]) => {
                this.setValidationResult(LocumsMarginCalcSections.EXPENSES, event, featureFlagEnabled);
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_MARGIN_NOTES_VALIDATION_RESULT)
            .pipe(withLatestFrom(this.enableSpecialtyPersistenceLocums1099$))
            .subscribe(([event, featureFlagEnabled]) => {
                this.setValidationResult(LocumsMarginCalcSections.MARGIN_NOTES, event, featureFlagEnabled);
            });

        // All validations completed successfully
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_ALL_VALIDATIONS_PASSED)
            .subscribe(() => this.onAllValidationsPassed());
    }

    private startValidation(action: 'save' | 'createPresentation' | 'contractRequest'): void {
        // Reset previous validation state
        this.currentAction = action;
        this.validationResults.clear();

        // Notify that validation process has started
        this.eventBus.emit({
            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_VALIDATION_STARTED
        });
    }

    private setValidationResult(section: string, event: EventPayload, featureFlagEnabled: boolean): void {
        // Store validation result for the corresponding section
        const result = event.payload as LocumsMarginCalculatorValidationResult;
        this.validationResults.set(section, result);

        // Check if all required validations are completed
        this.checkAllValidationsComplete(featureFlagEnabled);
    }

    private onAllValidationsPassed(): void {
        const action = this.currentAction;
        this.currentAction = null;

        if (action === 'save') {
            this.saveMarginDetails(this.marginCalculatorStore.getSnapshot());
        } else if (action === 'createPresentation') {
            // not yet implemented; handled in other tickets
        } else if (action === 'contractRequest') {
            this.eventBus.emit({
                type: LocumsContractRequestsEvents.LOCUMS_CONTRACT_REQUEST_DIALOG_OPEN,
                payload: {
                    marginPipelineRequestId: null,
                    source: 'margin-calculator'
                }
            });
        }
    }

    private checkAllValidationsComplete(featureFlagEnabled: boolean) {
        // Check if we have results from the sections
        if (this.validationResults.size > 0) {
            const validResult: LocumsMarginCalculatorValidationResult = {
                isValid: true,
                section: '',
                errors: []
            };
            const candidateResult = featureFlagEnabled
                ? (this.validationResults.get(LocumsMarginCalcSections.CANDIDATE) ?? validResult)
                : validResult;
            const marginSetupResult = this.validationResults.get(LocumsMarginCalcSections.MARGIN_SETUP);
            const additionalRatesResult = this.validationResults.get(LocumsMarginCalcSections.ADDITIONAL_RATES);
            const marginNotesResult = this.validationResults.get(LocumsMarginCalcSections.MARGIN_NOTES);
            const expensesResult = this.validationResults.get(LocumsMarginCalcSections.EXPENSES);

            if (
                !marginSetupResult ||
                !additionalRatesResult ||
                !marginNotesResult ||
                !expensesResult ||
                !candidateResult
            ) {
                return;
            }

            const allValid =
                marginSetupResult.isValid &&
                additionalRatesResult.isValid &&
                marginNotesResult.isValid &&
                expensesResult.isValid &&
                candidateResult.isValid;
            if (allValid) {
                this.eventBus.emit({
                    type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_ALL_VALIDATIONS_PASSED
                });
            } else {
                this.emitEventToScrollToFirstValidationError(
                    marginSetupResult,
                    additionalRatesResult,
                    marginNotesResult,
                    expensesResult,
                    candidateResult
                );
            }
        }
    }

    private emitEventToScrollToFirstValidationError(
        marginSetupResult: LocumsMarginCalculatorValidationResult,
        additionalRatesResult: LocumsMarginCalculatorValidationResult,
        marginNotesResult: LocumsMarginCalculatorValidationResult,
        expensesResult: LocumsMarginCalculatorValidationResult,
        candidateResult: LocumsMarginCalculatorValidationResult
    ) {
        if (!candidateResult.isValid) {
            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SCROLL_TO_VALIDATION_ERROR,
                payload: LocumsMarginCalcSections.CANDIDATE
            });
        } else if (!marginSetupResult.isValid) {
            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SCROLL_TO_VALIDATION_ERROR,
                payload: LocumsMarginCalcSections.MARGIN_SETUP
            });
        } else if (!additionalRatesResult.isValid) {
            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SCROLL_TO_VALIDATION_ERROR,
                payload: LocumsMarginCalcSections.ADDITIONAL_RATES
            });
        } else if (!marginNotesResult.isValid) {
            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SCROLL_TO_VALIDATION_ERROR,
                payload: LocumsMarginCalcSections.MARGIN_NOTES
            });
        } else if (!expensesResult.isValid) {
            this.eventBus.emit({
                type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SCROLL_TO_VALIDATION_ERROR,
                payload: LocumsMarginCalcSections.EXPENSES
            });
        }
    }

    private listenToLocums1099MarginCalculatorSaveSucceededEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_SUCCEEDED)
            .subscribe((event: EventPayload) => {
                this.getMarginDetails(event);
            });
    }

    private listenToLocums1099MarginPipelineRequestSaveSucceededEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PIPELINE_REQUEST_SAVE_SUCCEEDED)
            .subscribe((event: EventPayload) => {
                this.getMarginDetails(event);
            });
    }

    private listenToLocums1099MarginPendingRequestSaveSucceededEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_PENDING_REQUEST_SAVE_SUCCEEDED)
            .pipe(
                withLatestFrom(this.novaLocumsLeave1099GmcDialog$),
                filter(([, isEnabled]) => isEnabled)
            )
            .subscribe(([event]) => {
                this.getMarginDetails(event);
            });
    }

    private listenToLocums1099MarginPipelineRequestUpdatedSucceededEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_UPDATE_PENDING_REQUEST_SAVE_SUCCEEDED)
            .subscribe((event: EventPayload) => {
                this.getMarginDetails(event);
            });
    }

    private getLookups() {
        this.additionalRatesEffects.getAdditionalRateTypes();
        this.additionalRatesEffects.getMarginCoverageTypes();
        this.marginExpensesEffects.getMarginExpenseTypes();
        this.marginCalculationsEffects.getMalpracticeInsuranceRates();
        this.marginCalculationsEffects.getTargetMargins();
        this.marginCalculationsEffects.getCommissionTiers();
    }

    readonly saveMarginDetails = rxMethod<LocumsMarginCalculatorSlice>((slice$) =>
        slice$.pipe(
            exhaustMap((slice) => {
                return this.marginCalculatorService.saveMarginDetails(slice).pipe(
                    tap((savedId: number) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_SUCCEEDED,
                            payload: { id: savedId }
                        });
                        this.toasterService.toast('The Margin has been saved.');
                    }),
                    catchError((error: Error) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_SAVE_FAILED,
                            payload: { error }
                        });
                        this.toasterService.toast('Error saving Margin');
                        return EMPTY;
                    })
                );
            })
        )
    );

    readonly getMarginDetails = rxMethod<EventPayload>((event$) =>
        event$.pipe(
            switchMap((event) =>
                this.enableCreatePresentation$.pipe(
                    take(1),
                    switchMap((flagEnabled) =>
                        forkJoin({
                            marginDetails: this.marginCalculatorService.getMarginDetails(event.payload.id),
                            showCreatePresentation: flagEnabled
                                ? this.marginCalculatorService
                                      .showCreatePresentation(event.payload.id)
                                      .pipe(catchError(() => of(false)))
                                : of(false)
                        })
                    ),
                    tap(({ marginDetails, showCreatePresentation }) => {
                        this.eventBus.emit({
                            type: LocumsMarginCalculatorEvents.LOCUMS_1099_MARGIN_CALCULATOR_LOAD_MARGIN_DETAILS,
                            payload: { marginDetails, showCreatePresentation }
                        });
                    }),
                    catchError(() => {
                        this.toasterService.toast('Error loading Margin Details');
                        return EMPTY;
                    })
                )
            )
        )
    );
}
